/*
 * Rebuy Smart Cart — recommendation-widget filtering, centralised on a global.
 * ----------------------------------------------------------------------------
 * The best-sellers and cross-sell widget templates used to inline their filter
 * logic as `.filter(function(){…})` expressions. That was duplicated and
 * fragile — one bad assumption about the metafield shape crashed the whole
 * widget render (Vue `renderList` throws → the widget mounts as `<!---->`).
 *
 * This exposes the filtering as `window.hrSmartCart`, which the templates call
 * from their `v-for` expressions (Rebuy's Vue compiles templates at runtime with
 * `with(this)`, so window globals resolve). Every method is crash-safe: any
 * unexpected data shape falls back to "keep the item" (never hide, never drop),
 * never throws.
 *
 * Inserted by snippets/async-load-head-scripts.liquid right before the Rebuy
 * scripts (loadCartScripts waits for it), so it is defined before Rebuy mounts a
 * widget; the theme editor loads it from layout/theme.liquid. Some template calls
 * still guard with `window.hrSmartCart ? … : <fallback>`.
 */
import {getDiscountLabel} from '@/scripts/utils/discountBadge';

(function () {
    var HR = (window.hrSmartCart = window.hrSmartCart || {});

    /*
     * Discount badge label for a Rebuy product ("15% OFF" / "Sale" / ''), driven by
     * the `discounted` and `discount:{percent}` tags. Used by every Rebuy template
     * that renders product cards.
     */
    HR.discountLabel = function (product) {
        return getDiscountLabel(product);
    };

    /*
     * One metafield off a Rebuy product/variant, as a plain value.
     *
     * Rebuy has been seen to return two shapes, so every reader in this file has
     * to cope with both: the namespace-keyed object
     * (`{ product_card: { option_label: <value | {value}> } }`) and the
     * documented array (`[{ namespace, key, value }]`). In the array shape an
     * entry that carries no namespace is matched on a dotted `key`, which is how
     * the inline reader this replaces behaved.
     *
     * Returns `undefined` when the namespace/key is not in the feed at all, so
     * callers can tell "absent" from an explicit falsy value and pick their own
     * default. Never throws.
     */
    function readMetafield(owner, namespace, key) {
        try {
            var mf = owner && owner.metafields;
            if (!mf) return undefined;

            if (Array.isArray(mf)) {
                var dotted = namespace + '.' + key;
                for (var i = 0; i < mf.length; i++) {
                    var m = mf[i];
                    if (!m) continue;
                    var match = m.namespace ? m.namespace === namespace && m.key === key : m.key === dotted;
                    if (!match) continue;
                    return m.value && typeof m.value === 'object' ? m.value.value : m.value;
                }
                return undefined;
            }

            if (typeof mf === 'object') {
                var ns = mf[namespace];
                var field = ns && typeof ns === 'object' ? ns[key] : undefined;
                if (field === undefined) return undefined;
                return field && typeof field === 'object' ? field.value : field;
            }

            return undefined;
        } catch (e) {
            return undefined;
        }
    }

    /*
     * Should a variant be HIDDEN from the smart-cart recommendation widget?
     * Driven by the variant metafield `smart_cart.recommendations`: an explicit
     * `true` hides the variant; anything else (absent / false / other) shows it.
     *
     * Reads through readMetafield(), so both Rebuy data shapes are handled.
     * Returns FALSE (don't hide) when the metafield is not present in the feed
     * at all, so the widgets keep showing every variant until the Rebuy data
     * source is configured to return it — only an explicit truthy value hides a
     * variant.
     */
    HR.isHidden = function (variant) {
        try {
            var val = readMetafield(variant, 'smart_cart', 'recommendations');
            if (val === undefined) return false; // key not in the feed → don't hide
            return val === true || val === 'true';
        } catch (e) {
            return false; // never hide on bad/unexpected data
        }
    };

    /*
     * Is this product a bundle — i.e. must it be configured on its PDP instead
     * of being added to cart from a widget card? Mirrors the theme-side rule in
     * snippets/bundle-checker.liquid: the product id is registered in the
     * FastBundle app config (published as `window.theme.bundleProductIds` from
     * layout/theme.liquid), or a `bundle.option_1` … `option_6` metafield
     * carries a value, or the product is tagged exactly `bundle`.
     *
     * The tag match is exact (not substring), so `holiday-bundle` / `unbundled`
     * are not treated as bundles — same as the Liquid cards.
     *
     * Handles both metafield shapes Rebuy has been seen to return (see
     * isHidden), and tags as either an array or a comma-separated string.
     * Returns FALSE on any unexpected shape: an add-to-cart button is the
     * status quo, so bad data degrades to today's behaviour rather than
     * blocking a purchase.
     */
    HR.BUNDLE_OPTION_KEYS = ['option_1', 'option_2', 'option_3', 'option_4', 'option_5', 'option_6'];

    HR.isBundle = function (product) {
        try {
            if (!product) return false;

            // FastBundle registry — the signal an actual FastBundle bundle
            // carries (it needs neither the tag nor the metafields). String
            // compare: Rebuy returns numeric ids, the app config may hold either.
            var ids = window.theme && window.theme.bundleProductIds;
            if (Array.isArray(ids) && product.id !== undefined && product.id !== null) {
                var wanted = String(product.id);
                for (var b = 0; b < ids.length; b++) {
                    if (String(ids[b]) === wanted) return true;
                }
            }

            // Tags: array of strings, or a comma-separated string.
            var tags = product.tags;
            if (typeof tags === 'string') tags = tags.split(',');
            if (Array.isArray(tags)) {
                for (var t = 0; t < tags.length; t++) {
                    var tag = tags[t];
                    if (typeof tag !== 'string') continue;
                    if (tag.trim().toLowerCase() === 'bundle') return true;
                }
            }

            var mf = product.metafields;
            if (!mf) return false;

            // Array shape: [{ namespace, key, value }]
            if (Array.isArray(mf)) {
                for (var i = 0; i < mf.length; i++) {
                    var m = mf[i];
                    if (!m || m.namespace !== 'bundle') continue;
                    if (HR.BUNDLE_OPTION_KEYS.indexOf(m.key) === -1) continue;
                    var av = m.value && typeof m.value === 'object' ? m.value.value : m.value;
                    if (av) return true;
                }
                return false;
            }

            // Object shape: { bundle: { option_1: <value | {value}> } }
            if (typeof mf === 'object') {
                var ns = mf.bundle;
                if (!ns || typeof ns !== 'object') return false;
                for (var k = 0; k < HR.BUNDLE_OPTION_KEYS.length; k++) {
                    var field = ns[HR.BUNDLE_OPTION_KEYS[k]];
                    if (field === undefined || field === null) continue;
                    var val = field && typeof field === 'object' ? field.value : field;
                    if (val) return true;
                }
            }

            return false;
        } catch (e) {
            return false; // never block add-to-cart on bad/unexpected data
        }
    };

    /*
     * Is a variant purchasable (in stock)?
     *
     * Prefers Rebuy's canonical `variantAvailable()` when the template passes it
     * (bound to the widget component); on any throw it falls through to the
     * variant's own data so filtering still works if the function is absent.
     * Data path: explicit `available` wins; when it isn't present we infer from
     * inventory (tracked, no oversell, zero-or-less → sold out). Any unexpected
     * shape returns TRUE (keep) — never hide a valid item on bad data, matching
     * isHidden()'s bias.
     */
    HR.isAvailable = function (variant, availableFn) {
        try {
            if (!variant) return false;

            if (typeof availableFn === 'function') {
                try {
                    return !!availableFn(variant);
                } catch (e) {
                    // fall through to variant data
                }
            }

            if (variant.available === false) return false;
            if (variant.available === true) return true;

            // `available` not in the feed — infer from inventory when tracked.
            if (
                variant.inventory_management &&
                variant.inventory_policy !== 'continue' &&
                typeof variant.inventory_quantity === 'number' &&
                variant.inventory_quantity <= 0
            ) {
                return false;
            }

            return true; // unknown → keep
        } catch (e) {
            return true;
        }
    };

    /*
     * The visible text for one variant-option value on a widget product card.
     *
     * JS twin of the Liquid rule in snippets/collection-product-card.liquid, so
     * the PLP cards and the Rebuy carousels label a size the same way. The
     * `product_card.option_label` metafield picks the mode, a variant overriding
     * its product:
     *
     *     size   show only the leading volume: "8ML TRAVEL SPRAY" → "8ML"
     *     full   show the whole option value
     *
     * Defaults to `size`; absent or unrecognised behaves as `size`, and `full`
     * is the opt-out. Shortening only applies when the leading token carries a
     * volume, so "4 x 1.5ML" and "Out Of Office Tote" stay whole either way.
     * Keep the two implementations in step — a change here needs the same change
     * in the snippet.
     *
     * Only the visible text changes. The caller keeps the full string as the
     * accessible name, and the value Rebuy selects the variant on is never
     * touched. Never throws: returns the value unchanged on any unexpected
     * shape, which is the whole label as it renders today.
     */
    HR.optionLabel = function (product, value) {
        try {
            if (typeof value !== 'string') return value;

            var mode = readMetafield(product && product.selected_variant, 'product_card', 'option_label');
            if (mode === undefined) {
                mode = readMetafield(product, 'product_card', 'option_label');
            }
            if (mode === 'full') return value;

            var trimmed = value.trim();
            var head = trimmed.split(/\s+/)[0];
            if (head !== trimmed && head.toLowerCase().indexOf('ml') !== -1) return head;

            return value;
        } catch (e) {
            return value;
        }
    };

    /*
     * Money without cents. Rebuy's formatMoney() always renders the shop's
     * default money format, which includes the `.00` fraction; the Smart Cart
     * recommendation design shows whole-currency prices. Pass a formatMoney()
     * result through here to drop the decimal separator + its 2-digit fraction,
     * currency-agnostically — handles "$120.00", "$1,299.00 USD" and "120,00 €"
     * (only the trailing fraction is stripped, thousands separators are kept).
     * Never throws: returns the input unchanged on any unexpected shape.
     */
    HR.noCents = function (formatted) {
        try {
            if (typeof formatted !== 'string') return formatted;
            return formatted.replace(/[.,]\d{2}(\D*)$/, '$1');
        } catch (e) {
            return formatted;
        }
    };

    /*
     * Money without a *zero* fraction. Like noCents, but only drops the
     * fraction when it is `.00` / `,00` — a real fraction is kept. Used for the
     * line-item + subtotal prices so whole-dollar amounts read "$50" while a
     * genuine "$49.99" is left intact. Currency-agnostic (only the trailing
     * fraction is touched; thousands separators + currency suffix are kept),
     * and never throws: returns the input unchanged on any unexpected shape.
     *   "$50.00" → "$50"   "$1,299.00 USD" → "$1,299 USD"   "$49.99" → "$49.99"
     */
    HR.noTrailingZeros = function (formatted) {
        try {
            if (typeof formatted !== 'string') return formatted;
            return formatted.replace(/([.,])00(\D*)$/, '$2');
        } catch (e) {
            return formatted;
        }
    };

    /*
     * Money without a zero fraction, anywhere INSIDE a string.
     *
     * noTrailingZeros is anchored to end-of-string, so it can only clean a bare
     * price. The tiered progress bar hands us a whole sentence instead —
     * getTierFeedbackText() returns the Admin-authored copy with
     * {{remaining_amount}} already run through Rebuy's formatMoney(), so the
     * amount sits mid-sentence and the end-anchored regex never reaches it.
     *
     * Only a *zero* fraction is dropped, and only when no digit follows, so
     * European thousands separators survive:
     *   "Spend $20.00 more"  → "Spend $20 more"
     *   "Spend 1.000,00 € more" → "Spend 1.000 € more"
     *   "Spend $49.99 more"  → unchanged
     * Never throws: returns the input unchanged on any unexpected shape.
     */
    HR.moneyNoTrailingZeros = function (text) {
        try {
            if (typeof text !== 'string') return text;
            return text.replace(/(\d)[.,]00(?!\d)/g, '$1');
        } catch (e) {
            return text;
        }
    };

    /*
     * Nested (bundle) line items: the parent's price.
     *
     * A Rebuy bundle adds a $0 parent line plus one child line per component, so
     * `itemPrice(parent)` renders "$0". The design (Figma 826-2293) shows the
     * bundle's real value on the parent row, so these sum the children instead.
     *
     * The result is divided by the parent quantity to give the price of ONE
     * bundle: child quantities are absolute (a 2-per-bundle component sits at 4
     * when the parent is 2), and every other row in the drawer shows a unit price
     * — Rebuy renders `itemPrice()`, so a $120 bottle reads "$120" at any
     * quantity. Without the division a bundle at qty 2 would read "$125" beside a
     * bottle reading "$120", which looks like a per-line total on one row and a
     * unit price on the next.
     *
     * `nestedPrice` sums the discounted price, `nestedCompareAtPrice` the
     * undiscounted one; both return cents for formatMoney(). Returns null when
     * the item has no children so the template falls back to Rebuy's own price.
     * Never throws: any unexpected shape returns null.
     */
    function sumChildren(item, field) {
        try {
            var children = item && item.children;
            if (!Array.isArray(children) || !children.length) return null;

            var bundles = typeof item.quantity === 'number' && item.quantity > 0 ? item.quantity : 1;

            var total = 0;
            for (var i = 0; i < children.length; i++) {
                var c = children[i];
                if (!c) continue;
                var unit = typeof c[field] === 'number' ? c[field] : c.price;
                if (typeof unit !== 'number') return null;
                total += unit * (typeof c.quantity === 'number' ? c.quantity : 1);
            }
            return Math.round(total / bundles);
        } catch (e) {
            return null;
        }
    }

    HR.nestedPrice = function (item) {
        return sumChildren(item, 'final_price');
    };

    HR.nestedCompareAtPrice = function (item) {
        return sumChildren(item, 'original_price');
    };

    /* Is the bundle discounted — i.e. worth showing a struck compare-at price? */
    HR.nestedHasDiscount = function (item) {
        var price = HR.nestedPrice(item);
        var compare = HR.nestedCompareAtPrice(item);
        return price !== null && compare !== null && compare > price;
    };

    /* Does this line item have nested children (i.e. is it a Rebuy bundle parent)? */
    HR.isBundleParent = function (item) {
        try {
            return !!(item && Array.isArray(item.children) && item.children.length);
        } catch (e) {
            return false;
        }
    };

    /*
     * Bundle quantity: keep a bundle's child line items in step with its parent.
     *
     * Rebuy's Bundle Builder adds the parent and each component as INDEPENDENT
     * Shopify lines, and its own quantity controls only touch the line you
     * clicked — bumping the parent to 2 leaves the children behind, so the cart
     * charges for one bundle's contents but says "2". This drives every line of
     * the bundle together.
     *
     * Each child's per-bundle count is derived from the current state
     * (`child.quantity / parent.quantity`) and re-applied against the new parent
     * quantity, rounded to the nearest whole unit so a hand-edited line (parent
     * 2, child 3) still moves sensibly instead of blocking the shopper.
     *
     * All the lines go up in ONE `/cart/update.js` request: Shopify applies it
     * atomically, so the cart is never briefly charging for a half-updated
     * bundle, and there are no stale item references (Rebuy replaces its item
     * objects on every re-render, so a sequential per-line loop would be
     * operating on dead handles by its second call).
     *
     * Returns a promise, and never rejects: on any failure it just refreshes the
     * cart so the drawer re-renders from Shopify's actual state rather than
     * leaving the UI showing a quantity that was never applied.
     */
    HR.setBundleQuantity = function (item, quantity) {
        var parentQty = item && typeof item.quantity === 'number' ? item.quantity : 0;
        var target = Math.max(0, Math.round(quantity) || 0);

        if (!HR.isBundleParent(item) || !item.key || parentQty <= 0) {
            return Promise.resolve(false);
        }

        var updates = {};
        updates[item.key] = target;

        for (var i = 0; i < item.children.length; i++) {
            var child = item.children[i];
            if (!child || !child.key || typeof child.quantity !== 'number') continue;

            // Per-bundle count, re-applied to the new parent quantity. Never
            // round a still-present child down to 0 — that would silently drop a
            // component out of the bundle.
            var perBundle = child.quantity / parentQty;
            updates[child.key] = target === 0 ? 0 : Math.max(1, Math.round(perBundle * target));
        }

        // Drives the template's spinner (it watches item.status).
        var previousStatus = item.status;
        item.status = target > parentQty ? 'increasing' : 'decreasing';

        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        return fetch(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: updates }),
        })
            .then(function (response) {
                if (!response.ok) throw new Error('cart/update.js failed: ' + response.status);
                return response.json();
            })
            .then(function () {
                return true;
            })
            .catch(function () {
                return false;
            })
            .then(function (ok) {
                item.status = previousStatus;
                return HR.refreshCart().then(function () {
                    return ok;
                });
            });
    };

    /*
     * Re-read the cart into Rebuy after a direct Shopify mutation. Prefers
     * Rebuy's own fetch (which re-renders the drawer); falls back to the
     * document event other cart listeners in the theme use. Never throws.
     */
    HR.refreshCart = function () {
        try {
            var cart = window.Rebuy && window.Rebuy.Cart;
            var result = cart && typeof cart.fetchCart === 'function' ? cart.fetchCart() : null;

            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

            return result && typeof result.then === 'function' ? result.catch(function () {}) : Promise.resolve();
        } catch (e) {
            return Promise.resolve();
        }
    };

    /*
     * Best-sellers: one card per product. Drops hidden (metafield-flagged)
     * variants and sold-out ones (checked against each product's
     * selected_variant, in both product- and variant-source modes).
     * opts: { variantAvailable } (pass Rebuy's method bound to `this`).
     */
    HR.visibleProducts = function (products, opts) {
        if (!Array.isArray(products)) return products || [];
        opts = opts || {};
        return products.filter(function (p) {
            var v = p && p.selected_variant;
            return !HR.isHidden(v) && HR.isAvailable(v, opts.variantAvailable);
        });
    };

    /*
     * Cross-sell: the variants to render for a product. In variant-source mode a
     * product expands to one card per variant; otherwise just its selected
     * variant. Hidden (metafield-flagged) and sold-out variants are dropped — so
     * a product keeps only its shown, in-stock variants (and renders no rows if
     * none remain).
     *
     * A bundle is the exception: it never expands per variant (its card is a
     * link to the PDP, so one row is all that makes sense) and it is not
     * availability-filtered, since a bundle's own selected_variant can read as
     * unavailable while the bundle is still configurable.
     *
     * opts: { metadata, variantAvailable } (pass Rebuy's method bound to `this`).
     */
    HR.visibleVariants = function (product, opts) {
        opts = opts || {};

        // Exactly one row, matching the template's own no-hrSmartCart fallback
        // shape — a bundle must stay visible even if its variant data is thin.
        if (HR.isBundle(product)) {
            return [product && product.selected_variant];
        }

        var variantSource = !!(opts.metadata && opts.metadata.custom && opts.metadata.custom.source === 'variant');
        var list = variantSource ? product && product.variants : [product && product.selected_variant];
        if (!Array.isArray(list)) list = list ? [list] : [];
        return list.filter(function (v) {
            return !HR.isHidden(v) && HR.isAvailable(v, opts.variantAvailable);
        });
    };
})();
